import type { DOMNode, Element } from "html-react-parser-v6";
import { safeHref } from "../link/link.href";
import { PHRASING } from "./text.shared";

/* The one gate for CMS markup shown inside a Tooltip panel. Text's text.tooltip
   asks it while building the panel, so the Tooltip's client file never imports
   it. The href guard comes from link.href, not link, so no Link stylesheet
   rides along. No client directive. */

export const VOID: ReadonlySet<string> = new Set(["br", "img", "wbr"]);

/* Inline text-level elements, and images. Anything interactive (button, input, label,
   select) or embedded (iframe, object) is not here, so it never reaches a panel
   that is read as a tooltip. */
export const KEEP: ReadonlySet<string> = new Set([
  "a", "abbr", "b", "bdi", "bdo", "br", "cite", "code", "del", "dfn", "em", "i",
  "img", "ins", "kbd", "mark", "q", "rp", "rt", "ruby", "s", "samp", "small",
  "span", "strong", "sub", "sup", "time", "u", "var", "wbr"
]);

/* Elements whose insides are not words a reader should get. */
export const DROP: ReadonlySet<string> = new Set([
  "applet", "audio", "canvas", "embed", "iframe", "math", "noscript", "object",
  "svg", "template", "video"
]);

export type TagKind = "keep" | "block" | "unwrap" | "drop";

/** keep: drawn with filtered attributes. block: its words only, set apart by
 *  spaces. unwrap: its words only. drop: nothing. */
export function tagKind(name: string): TagKind {
  if (KEEP.has(name)) {
    return "keep";
  }
  if (DROP.has(name)) {
    return "drop";
  }
  return PHRASING.has(name) ? "unwrap" : "block";
}

const GLOBAL_ATTRIBUTE = /^(?:lang|aria-label)$/;
const IMG_TEXT_ATTRIBUTES = new Set(["alt", "width", "height"]);
/* The fields GTM reads off a clicked link (atoms/link/link.stories.tsx). */
const LINK_REPORTING = new Set(["data-linkname", "data-linkposition", "data-linkdestination"]);
/* The one class a panel keeps: words written for a screen reader only. */
const SR_ONLY = "dds-sr-only";

/**
 * The attributes a kept element may carry, by an allowlist: every tag takes
 * `lang` and `aria-label` (no other aria-*: aria-hidden or id-pointing ones
 * would hide a link or reach page ids), and `class` only as `dds-sr-only`
 * (returned as `className`); `a` takes a safe `href`, `title`, `target="_blank"`
 * (with the `rel` it needs, added here, never taken from the copy) and the
 * `data-link*` fields GTM reports; `img` takes `alt`, `width`, `height` and a
 * safe `src`. Everything else — style, other classes, id, ping, srcset,
 * handlers — is dropped. Values arrive decoded. Hidden elements never get
 * here: text.tooltip drops them whole.
 */
export function cleanAttributes(element: Element): Record<string, string> {
  const kept: Record<string, string> = {};
  const tag = element.name;
  for (const [key, raw] of Object.entries(element.attribs ?? {})) {
    const name = key.toLowerCase();
    const value = raw ?? "";
    if (GLOBAL_ATTRIBUTE.test(name)) {
      kept[name] = value;
    } else if (name === "class" && value.split(/\s+/).includes(SR_ONLY)) {
      kept.className = SR_ONLY;
    } else if (tag === "a" && (name === "title" || LINK_REPORTING.has(name))) {
      kept[name] = value;
    } else if (tag === "a" && name === "href" && safeHref(value) !== "#") {
      kept[name] = safeHref(value);
    } else if (tag === "img" && IMG_TEXT_ATTRIBUTES.has(name)) {
      kept[name] = value;
    } else if (tag === "img" && name === "src" && safeHref(value) !== "#") {
      kept[name] = safeHref(value);
    }
  }
  if (tag === "a" && kept.href && element.attribs?.target === "_blank") {
    kept.target = "_blank";
    kept.rel = "noopener noreferrer";
  }
  return kept;
}

/** Whether an element is a link a reader can follow. */
export function isLink(element: Element): boolean {
  return element.name === "a" && Boolean(cleanAttributes(element).href);
}

/** Whether a node sits inside a followable link, where a second one is invalid. */
export function insideLink(node: DOMNode): boolean {
  let ancestor = node.parent as Element | null;
  while (ancestor) {
    if (isLink(ancestor)) {
      return true;
    }
    ancestor = ancestor.parent as Element | null;
  }
  return false;
}
